import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TopBar from "../components/TopBar";
import RepoDropdown from "../components/RepoDropdown";
import { ReactComponent as BackIcon } from "../assets/icon_back.svg";
import { useRemoteFileActionsContext } from "../contexts/RemoteFileActionsContext";

function ProjectDetailScreen() {
  const { projectId } = useParams();
  const { projects, updateProject, deleteProject } =
    useContext(FirebaseContext);
  const { fetchRepos } = useRemoteFileActionsContext();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [reposList, setReposList] = useState([]);
  const [selectedRepoUrl, setSelectedRepoUrl] = useState("");

  const hasFetchedRepos = useRef(false);
  const nameTimeoutRef = useRef(null);
  const descriptionTimeoutRef = useRef(null);

  // Fetch the project from the projects list using projectId
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const selectedProject = projects.find((p) => String(p.id) === projectId);
      if (selectedProject) {
        setProject(selectedProject);
        setProjectName(selectedProject.projectName || "");
        setProjectDescription(selectedProject.projectDescription || "");

        // Check if gitRepo field has a value and extract the repo name
        if (selectedProject.gitRepo) {
          const repoName = selectedProject.gitRepo.split("/").pop();
          setSelectedRepoUrl(selectedProject.gitRepo); // Set the full URL for `selectedRepoUrl`
        }
      } else {
        console.log("No project found with the given projectId.");
      }
    }
  }, [projectId, projects]);

  // Fetch repos only once per screen visit
  useEffect(() => {
    if (!hasFetchedRepos.current) {
      // TODO uncomment?
      // startFetchingRepos();
      hasFetchedRepos.current = true; // Prevent further fetches on this visit
    }
  }, []); // Empty dependency array to ensure it only runs once per visit

  // Debounce function for updating name or description
  const debounceUpdateProject = (field, value, timeoutRef) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      console.log(`Updating ${field} in Firestore:`, value);
      updateProject(projectId, { [field]: value });
    }, 500); // 500ms debounce
  };

  const startFetchingRepos = async () => {
    console.log("Fetching repos...");
    try {
      const reposResponse = await fetchRepos();
      console.log("Repos fetched:", reposResponse.repos);
      setReposList(reposResponse.repos);
    } catch (error) {
      console.error("Error fetching repos:", error);
    }
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setProjectName(newName);
    debounceUpdateProject("projectName", newName, nameTimeoutRef);
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setProjectDescription(newDescription);
    debounceUpdateProject(
      "projectDescription",
      newDescription,
      descriptionTimeoutRef
    );
  };

  const handleRepoSelection = (repoUrl) => {
    setSelectedRepoUrl(repoUrl);
    updateProject(projectId, { gitRepo: repoUrl });
  };

  // TODO ken delete related tasks & recrods as well
  const handleDeleteProject = async () => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      await deleteProject(projectId);
      navigate(-1); // Navigate back
    }
  };

  // Cleanup timeouts when the component unmounts
  useEffect(() => {
    return () => {
      if (nameTimeoutRef.current) {
        clearTimeout(nameTimeoutRef.current);
      }
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, []);

  if (!project) {
    return <div>Loading project details...</div>;
  }

  return (
    <div
      style={{
        paddingTop: "60px",
      }}
    >
      <TopBar
        title={projectName}
        leftContainer={
          <button
            onClick={() => navigate(-1)}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <BackIcon
              style={{
                width: "24px",
                height: "24px",
                filter:
                  "brightness(0) saturate(100%) invert(19%) sepia(7%) saturate(1119%) hue-rotate(180deg) brightness(90%) contrast(94%)",
              }}
            />
          </button>
        }
      />
      <div style={{ padding: "16px" }}>
        {/* Project Name Input */}
        <div>
          <label htmlFor="projectName">Project Name:</label>
          <input
            type="text"
            id="projectName"
            value={projectName}
            onChange={handleNameChange}
            style={{
              width: "100%",
              padding: "8px",
              margin: "8px 0",
              borderRadius: "4px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--text)",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Project Description Input */}
        <div>
          <label htmlFor="projectDescription">Project Description:</label>
          <textarea
            id="projectDescription"
            value={projectDescription}
            onChange={handleDescriptionChange}
            rows="4"
            style={{
              width: "100%",
              height: "120px",
              padding: "8px",
              margin: "8px 0",
              borderRadius: "4px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--text)",
              boxSizing: "border-box",
            }}
          />
        </div>

        <RepoDropdown
          reposList={reposList}
          selectedRepoUrl={selectedRepoUrl}
          onSelectRepo={handleRepoSelection} // Pass the handler to update gitRepo
        />

        <button
          onClick={handleDeleteProject}
          style={{
            marginTop: "32px",
            backgroundColor: "#ff4d4d",
            color: "#fff",
            border: "none",
            padding: "12px 16px",
            borderRadius: "8px",
            cursor: "pointer",
            width: "100%",
          }}
        >
          Delete Project
        </button>
      </div>
    </div>
  );
}

export default ProjectDetailScreen;
